import type { ReactElement } from 'react';
import styled from 'styled-components';
import { Modal } from '../../../components';
import type { Installer } from '../../../model';
import { useForm } from './form';

interface Props {
  installer?: Installer;
  show: boolean;
  refetch: () => Promise<unknown>;
  onClose: () => void;
}

const SOFTWARE_OPTIONS = ['LCS', 'EMS', 'Updater'];

const Intro = styled.p`
  margin: 2px 2px 6px;
  font-size: 13.5px;
  font-weight: 500;
  line-height: 1.55;
  color: rgba(176, 186, 209, 0.72);
`;

const Row = styled.div`
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
`;

const Field = styled.div`
  flex: 1 1 0%;
  min-width: 150px;
`;

const ExpirationField = styled.div`
  flex: 0 0 auto;
`;

const Label = styled.div`
  font-weight: 600;
  font-size: 9px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(176, 186, 209, 0.5);
  margin-bottom: 6px;
`;

const inputStyles = `
  width: 100%;
  background: rgba(8, 14, 30, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: rgb(240, 242, 245);
  padding: 9px 11px;
  font-weight: 600;
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const TextInput = styled.input`
  ${inputStyles}
`;

const ExpirationInput = styled.input`
  ${inputStyles}
  width: 84px;
  text-align: center;
`;

const NoteTextarea = styled.textarea`
  ${inputStyles}
  min-height: 72px;
  font-weight: 400;
  font-size: 12px;
  resize: vertical;
`;

const SelectField = styled.div`
  position: relative;

  select {
    width: 100%;
    appearance: none;
    -webkit-appearance: none;
    background: rgba(8, 14, 30, 0.5);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    color: rgb(240, 242, 245);
    padding: 9px 30px 9px 11px;
    font-weight: 600;
    font-size: 13px;
    cursor: pointer;
  }

  select:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.2);
  }

  option {
    background: rgb(13, 23, 51);
    color: rgba(213, 221, 238, 0.92);
  }

  option:hover,
  option:checked {
    background: #1967d2;
    color: rgb(255, 255, 255);
  }

  .chev {
    position: absolute;
    right: 11px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    font-size: 11px;
    color: rgba(176, 186, 209, 0.6);
  }
`;

const ErrorText = styled.div`
  margin-top: 5px;
  font-weight: 500;
  font-size: 11px;
  color: rgb(255, 138, 147);
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
`;

const Button = styled.button<{ $variant: 'primary' | 'secondary' }>`
  border-radius: 9px;
  font-weight: 600;
  font-size: 13px;
  padding: 9px 18px;
  cursor: pointer;
  transition: all var(--transition);

  ${({ $variant }): string =>
    $variant === 'secondary'
      ? `
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.12);
        color: rgba(213, 221, 238, 0.92);
      `
      : `
        background: rgba(78, 95, 253, 0.16);
        border: 1px solid rgba(120, 130, 255, 0.4);
        color: rgb(159, 176, 255);
      `}

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }

  &:hover:not(:disabled),
  &:focus {
    outline: none;
    filter: brightness(1.1);
  }
`;

export const UpsertInstallerModal = ({
  show,
  installer,
  refetch,
  onClose
}: Props): ReactElement => {
  const { formik, loading } = useForm({ onClose, installer });

  const softwareError = formik.touched.software === true ? formik.errors.software : undefined;
  const nameError = formik.touched.name === true ? formik.errors.name : undefined;
  const urlError = formik.touched.url === true ? formik.errors.url : undefined;

  const expirationError =
    formik.touched.expirationTime === true ? formik.errors.expirationTime : undefined;

  const handleSave = (): void => {
    void (async (): Promise<void> => {
      formik.setFieldTouched('software');
      await formik.submitForm();
      await refetch();
    })();
  };

  return (
    <Modal
      open={show}
      onClose={onClose}
      title={`${installer ? 'Update' : 'Add'} Installer File`}
      width={560}
      footer={
        <Actions>
          <Button type='button' $variant='secondary' disabled={loading} onClick={onClose}>
            Cancel
          </Button>
          <Button type='button' $variant='primary' disabled={loading} onClick={handleSave}>
            {loading ? 'Saving…' : 'Save'}
          </Button>
        </Actions>
      }
    >
      <Intro>Provide information regarding the installer file.</Intro>

      <Row>
        <Field>
          <Label>Software</Label>
          <SelectField>
            <select
              value={formik.values.software}
              onBlur={(): void => {
                formik.setFieldTouched('software');
              }}
              onChange={(event): void => {
                formik.setFieldValue('software', event.target.value);
              }}
            >
              <option value=''>Software…</option>
              {SOFTWARE_OPTIONS.map((software) => (
                <option key={software} value={software}>
                  {software}
                </option>
              ))}
            </select>
            <i className='fas fa-chevron-down chev' />
          </SelectField>
          {softwareError !== undefined && <ErrorText>{softwareError}</ErrorText>}
        </Field>

        <ExpirationField>
          <Label>URL Expiration (min)</Label>
          <ExpirationInput type='number' {...formik.getFieldProps('expirationTime')} />
          {expirationError !== undefined && <ErrorText>{expirationError}</ErrorText>}
        </ExpirationField>
      </Row>

      <Field>
        <Label>Name</Label>
        <TextInput type='text' {...formik.getFieldProps('name')} />
        {nameError !== undefined && <ErrorText>{nameError}</ErrorText>}
      </Field>

      <Field>
        <Label>URL</Label>
        <TextInput type='text' {...formik.getFieldProps('url')} />
        {urlError !== undefined && <ErrorText>{urlError}</ErrorText>}
      </Field>

      <Field>
        <Label>Notes</Label>
        <NoteTextarea {...formik.getFieldProps('notes')} />
      </Field>
    </Modal>
  );
};
